import {
  ShaderMaterial,
  Color,
  Vector3,
  TextureLoader,
  RepeatWrapping
} from "three";

import vertex from "./materialBespokeVertex.glsl";
import fragment from "./materialBespokeFragment.glsl";

export default class MaterialBespoke {
  constructor() {
    // Set Directional Light Colours
    this.DIRECTIONAL_COLOURS = [
      new Color(
        1.5582857142857143 * 1.0,
        1.5582857142857143 * 1.0,
        1.5582857142857143 * 1.0
      ),
      new Color(
        1.3418571428571429 * 1.0,
        1.3418571428571429 * 0.98,
        1.3418571428571429 * 0.95
      )
    ];

    // Initialise Directional Light Directions
    this.DIRECTIONAL_DIRECTIONS = [
      new Vector3(0.0, 0.0, 0.0),
      new Vector3(0.0, 0.0, 0.0)
    ];

    // Uniforms
    const UNIFORMS = {
      // UV
      uUvScaleFabric: { type: "f", value: 6.071642857142857 },

      // Diffuse
      uSamplerDiffuse: { type: "t", value: null },

      // Diffuse Adjust
      uDiffuseAdjustHSV: {
        type: "v3",
        value: new Vector3(-0.013912767465897868, 0.909, -0.04771428571428571)
      },

      // Normals Fabric
      uSamplerNormalFabric: { type: "t", value: null },
      uNormalIntensityFabric: { type: "f", value: 0.9883571428571429 },

      // Normals Object
      uSamplerNormalObject: { type: "t", value: null },
      uNormalIntensityObject: { type: "f", value: 0.9595000000000001 },

      // Light Map
      uSamplerLightMap: { type: "t", value: null },

      uLightMapFresnelIntensity: { type: "f", value: 0.9306428571428571 },
      uLightMapDiffuseIntensity: { type: "f", value: 0.808 },
      uLightMapSpecularIntensity: { type: "f", value: 1.0 },

      // Ambient Light
      uAmbientLightColor: { type: "c", value: new Color(1.0, 1.0, 1.0) },

      // Directional Lights
      uDirectionalLightPositions: {
        type: "v3",
        value: this.DIRECTIONAL_DIRECTIONS
      },

      uDirectionalLightColours: { type: "v3", value: this.DIRECTIONAL_COLOURS },

      // Fresnel
      uFresnelColour: {
        type: "c",
        value: new Color(1.0, 0.505, 0.4977857142857143)
      },
      uFresnelIntensity: { type: "f", value: 0.9450714285714286 },
      uFresnelBias: { type: "f", value: 0.2525 },
      uFresnelScale: { type: "f", value: 0.7502857142857143 },
      uFresnelPower: { type: "f", value: 2.4889285714285716 },

      // Anisotropic
      uAnisotropicSpecularColor: {
        type: "c",
        value: new Color(1.0, 0.3607142857142857, 0.4472857142857143)
      },
      uAnisotropicShininess: { type: "f", value: 4.328571428571429 },
      uAnisotropicGroove: { type: "f", value: 0.16592857142857143 },
      uAnisotropicKd: { type: "f", value: 0.35350000000000004 },
      uAnisotropicKs: { type: "f", value: 0.202 },

      // Camera Position
      uCameraPosition: { type: "v3", value: new Vector3(0.0, 0.0, 0.0) },

      // DEV - Mode
      uRenderMode: { type: "i", value: 0 }
    };

    // Create Material
    this.MATERIAL = new ShaderMaterial({
      uniforms: UNIFORMS,
      vertexShader: vertex,
      fragmentShader: fragment,
      extensions: {
        derivatives: true
      }
    });

    const PATH = "https://lab.pikcells.com/assets/";

    // Load Normal Map Object
    this.loadNormalsObject(`${PATH}models/sofa/AURORA-2SEATER-NM.jpg`);

    // Load Light Map Object
    this.loadTextureLightMap(`${PATH}models/sofa/AURORA-2SEATER-LM.jpg`);

    // Load Diffuse Map Fabric
    this.loadNormalsFabric(`${PATH}textures/velvetrouge/normal.jpg`);

    // Load Normal Map Fabric
    this.loadDiffuseFabric(`${PATH}textures/velvetrouge/diffuse.jpg`);
  }

  // ___________________________________________________________ Camera Position

  updateCameraPosition(position) {
    const SCALAR = 100.0;

    this.MATERIAL.uniforms.uCameraPosition.value = new Vector3(
      position.x * SCALAR,
      position.y * SCALAR,
      position.z * SCALAR
    );
  }

  // ________________________________________________________ Directional Lights

  updateDirectionalLightDirections(directionA, directionB) {
    const UNIFORMS = this.MATERIAL.uniforms;

    this.DIRECTIONAL_DIRECTIONS = [directionA, directionB];

    UNIFORMS.uDirectionalLightPositions.value = this.DIRECTIONAL_DIRECTIONS;
  }

  // ____________________________________________________________________ Object

  loadTextureLightMap(url) {
    const TEXTURE = new TextureLoader().load(url);
    this.MATERIAL.uniforms.uSamplerLightMap.value = TEXTURE;
  }

  loadNormalsObject(url) {
    const TEXTURE = new TextureLoader().load(url);
    this.MATERIAL.uniforms.uSamplerNormalObject.value = TEXTURE;
  }

  // ____________________________________________________________ Fabric Texture

  loadDiffuseFabric(url) {
    const TEXTURE = new TextureLoader().load(
      url,
      this.onDiffuseFabricLoad.bind(this)
    );
    TEXTURE.wrapS = RepeatWrapping;
    TEXTURE.wrapT = RepeatWrapping;
  }

  onDiffuseFabricLoad(texture) {
    this.MATERIAL.uniforms.uSamplerDiffuse.value = texture;
  }

  loadNormalsFabric(url) {
    const TEXTURE = new TextureLoader().load(
      url,
      this.onNormalsFabricLoad.bind(this)
    );
    TEXTURE.wrapS = RepeatWrapping;
    TEXTURE.wrapT = RepeatWrapping;
  }

  onNormalsFabricLoad(texture) {
    this.MATERIAL.uniforms.uSamplerNormalFabric.value = texture;
  }

  // ____________________________________________________________________ Access

  getMaterial() {
    return this.MATERIAL;
  }
}
